import format from 'date-fns/format';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Pagination,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { Feature } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Page } from '@qualifio/api-client';
import {
  PromocodeAttributes,
  PromocodeImportAttributes,
} from '@loyalty/shared-common/rewards-api-client';
import {
  QlfConfirmationDialog,
  colorPalette,
  useAlerts,
  useConfirmationDialog,
} from '@qualifio/ui-commons';
import { Trans, useTranslation } from 'react-i18next';
import { faCalendar } from '@fortawesome/pro-solid-svg-icons/faCalendar';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faDownload } from '@fortawesome/pro-solid-svg-icons/faDownload';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';
import { DeleteUnwonPromocodes } from './DeleteUnwonPromocodes';
import { PromocodeListItem } from './PromocodeListItem';
import { PromocodesWon } from './PromocodesWon';
import { SearchPromocodes } from './SearchPromocodes';
import { SxStyles } from '../../../shared/utils/types';
import { Title } from '../../../shared/layout/Title';
import { UploadPromocodes } from './UploadPromocodes';
import { useAccounts } from '../../../accounts/AccountsContext';
import { useApi } from '../../../shared/api-client/ApiClientProvider';
import { useFeatures } from '../../../features/FeaturesContext';
import { useFileDownload } from '../../../shared/use-file-download';
import { useProgram } from '../../../loyalty/ProgramContext';
import { useRewards } from '../../RewardsContext';

const styles: SxStyles = {
  statusIconPlaceholder: {
    width: '1.25em',
    display: 'inline-block',
    marginRight: 0.8,
  },
};

export function PromocodeImportList({ rewardId }: { rewardId: string }) {
  const { features } = useFeatures();
  const { enabled: rewardExportEnabled } = features[Feature.EXPORT_REWARDS];
  const { selectedReward, replaceReward } = useRewards();
  const { currentAccount } = useAccounts();
  const { program } = useProgram();
  const { t, i18n } = useTranslation();
  const { notify } = useAlerts();

  const [initialized, setInitialized] = useState(false);
  const [promocodeImportPageNumber, setPromocodeImportPageNumber] = useState<number>(1);
  const [page, setPage] = useState<Page<PromocodeImportAttributes> | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>();
  const enableWonExport = rewardExportEnabled && !!selectedReward?.promocodeStats?.wonCodes;
  const { download, downloading: exporting, anchorRef: exportAnchorRef } = useFileDownload();

  const { rewardsApi, profileApi } = useApi();

  // get promocodes by profile OR search
  async function doSearch(pageNumber: number, search?: string): Promise<Page<PromocodeAttributes>> {
    const profilesPage = search
      ? await profileApi.searchProfiles({
          params: {
            search,
            accountId: currentAccount.id,
            programId: program.id,
          },
        })
      : undefined;

    return rewardsApi.getPromocodes(rewardId, {
      params: {
        profileIds: profilesPage?.data?.map(p => p.id),
        search: search && search.length >= 3 ? search : undefined,
        pageNumber,
      },
    });
  }

  const refreshPromocodes = useCallback(() => {
    setLoading(true);
    setError(undefined);
    rewardsApi
      .getPromocodeImports(rewardId, {
        pagination: { pageNumber: promocodeImportPageNumber - 1, pageSize: 10 },
      })
      .then(setPage)
      .catch(setError)
      .finally(() => {
        setLoading(false);
        setInitialized(true);
      });
  }, [currentAccount.id, program.id, rewardId, promocodeImportPageNumber]);

  const refreshReward = useCallback(() => {
    rewardsApi
      .getReward(rewardId, { params: { withDeleted: true } })
      .then(reward => {
        replaceReward(reward);
      })
      .catch(() => notify(t('rewards.promo-codes.errors.refresh-reward'), 'error'));
  }, [rewardId]);

  const replacePromocodeImport = useCallback((newPromocodeImport: PromocodeImportAttributes) => {
    setPage(page => {
      if (!page) {
        return undefined;
      } else {
        return {
          ...page,
          type: 'page',
          data: page.data.map(promocodeImport =>
            promocodeImport.id === newPromocodeImport.id ? newPromocodeImport : promocodeImport,
          ),
        };
      }
    });
  }, []);

  const { confirmationDialogState, setConfirmationDialogState } = useConfirmationDialog();
  const openDialog = (pci: PromocodeImportAttributes) => {
    setConfirmationDialogState({
      title: t('rewards.promo-codes.confirm.deletion.title'),
      content: t('rewards.promo-codes.confirm.import-deletion.message'),
      confirmButtonText: t('generic.actions.hard-delete'),
      cancelButtonText: t('generic.actions.cancel'),
      open: true,
      onConfirm: () => deleteUnwonForImport(pci.id),
    });
  };

  const deleteUnwonForImport = useCallback(
    (promocodeImportId: string) => {
      rewardsApi
        .deleteUnwonPromocodes(rewardId, { params: { promocodeImportId } })
        .then(() => {
          notify(t('rewards.promo-codes.message.delete-unwon-for-import'), 'success');
          refreshReward();
          refreshPromocodes();
        })
        .catch(() => notify(t('rewards.promo-codes.errors.delete-unwon'), 'error'));
    },
    [rewardId, notify, refreshReward, refreshPromocodes],
  );

  const onPromocodeDelete = useCallback(() => {
    refreshReward();
    refreshPromocodes();
  }, [refreshReward, refreshPromocodes]);

  useEffect(() => {
    refreshPromocodes();
  }, [refreshPromocodes]);

  if (!selectedReward) {
    throw new Error('No reward selected!');
  }

  const downloadExport = useCallback(
    () => download(() => profileApi.exportWonRewards(currentAccount.id, selectedReward.id)),
    [download, profileApi, currentAccount.id, selectedReward.id],
  );

  const rewardName =
    selectedReward.i18nAttributes[i18n.language]?.name ||
    (Object.values(selectedReward.i18nAttributes || {}) || []).pop()?.name ||
    'unknown reward';

  return (
    <Box>
      <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
        <Title>{t('rewards.promo-codes.title')}</Title>
        <PromocodesWon
          won={selectedReward?.promocodeStats?.wonCodes}
          total={selectedReward?.promocodeStats?.totalCodes}
        />
      </Box>
      <p>
        <Trans
          t={t}
          i18nKey="rewards.promo-codes.message.intro"
          values={{ name: rewardName || 'reward' }}
          components={{ b: <strong /> }}
        />
      </p>
      <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
        <UploadPromocodes
          rewardId={rewardId}
          onUploaded={refreshPromocodes}
          onUpdate={replacePromocodeImport}
          onFinish={refreshReward}
        />
        <div>
          <Button
            disabled={!enableWonExport || exporting}
            color="secondary"
            startIcon={<FontAwesomeIcon icon={faDownload} />}
            sx={{ marginRight: '10px' }}
            onClick={downloadExport}
          >
            {t('rewards.promo-codes.button.export-won')}
          </Button>
          <a ref={exportAnchorRef} style={{ display: 'none' }} />
          <SearchPromocodes
            inputPlaceholder={t('rewards.promo-codes.search.message.search')}
            doSearch={doSearch}
            onDelete={onPromocodeDelete}
            ItemComponent={PromocodeListItem}
          />
        </div>
      </Box>
      <Box pt={1}>
        {loading ? (
          <Skeleton />
        ) : error ? (
          <Alert color="error">{error.message}</Alert>
        ) : (
          <Table
            sx={{
              '& .MuiTableCell-root': {
                padding: 0.8,
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>
                  <FontAwesomeIcon
                    icon={faCalendar}
                    title={t('rewards.promo-codes.table.headers.date')}
                  />
                </TableCell>
                <TableCell>{t('rewards.promo-codes.table.headers.filename')}</TableCell>
                <TableCell>{t('rewards.promo-codes.table.headers.used')}</TableCell>
                <TableCell>{t('rewards.promo-codes.table.headers.status')}</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {page?.data.length === 0 && (
                <Box display="flex" justifyContent="center">
                  {t('rewards.promo-codes.message.empty-list')}
                </Box>
              )}
              <QlfConfirmationDialog {...confirmationDialogState} />
              {page?.data.map(pci => (
                <TableRow key={pci.id}>
                  <TableCell>{format(new Date(pci.createdAt), 'dd/MM/yyyy HH:mm')}</TableCell>
                  <TableCell>{pci.originalName}</TableCell>
                  <TableCell
                    sx={pci.usedCodes > 0 ? { color: colorPalette.orange.main } : undefined}
                  >
                    {pci.usedCodes} / {pci.totalLines}
                  </TableCell>
                  <TableCell>
                    {pci.error ? (
                      <Box component="p" sx={{ color: colorPalette.red.main }}>
                        <Box component="span" sx={styles.statusIconPlaceholder} />
                        {t('rewards.promo-codes.status.error')}
                      </Box>
                    ) : pci.done ? (
                      pci.usedCodes >= pci.totalLines ? (
                        <Box component="p" sx={{ color: colorPalette.orange.main }}>
                          <Box component="span" sx={styles.statusIconPlaceholder} />
                          {t('rewards.promo-codes.status.noneAvailable')}
                        </Box>
                      ) : (
                        <Box component="p" sx={{ color: colorPalette.green.main }}>
                          <Box
                            component={FontAwesomeIcon}
                            icon={faCheck}
                            sx={{ marginRight: 0.8 }}
                            fixedWidth
                          />
                          {t('rewards.promo-codes.status.done')}
                        </Box>
                      )
                    ) : (
                      <Box component="p" sx={{ color: colorPalette.orange.main }}>
                        <Box component="span" sx={styles.statusIconPlaceholder} />
                        {t('rewards.promo-codes.status.importing')}
                        &nbsp;({Math.round((pci.totalLinesImported / pci.totalLines) * 100)}%)
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {pci.usedCodes < pci.totalLines && (
                      <IconButton
                        disabled={loading}
                        onClick={() => openDialog(pci)}
                        sx={{
                          color: colorPalette.grey.main,
                          minWidth: 40,
                          '&:hover': {
                            backgroundColor: 'inherit',
                            color: colorPalette.turquoise.main,
                          },
                        }}
                      >
                        <FontAwesomeIcon size="xs" icon={faTrash} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
      {initialized && (
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          pt={1}
        >
          <Box>
            <DeleteUnwonPromocodes
              rewardId={rewardId}
              onDeleted={promocodeStats => {
                replaceReward({ ...selectedReward, promocodeStats });
                refreshPromocodes();
              }}
            />
          </Box>
          <Box pt={1}>
            <Pagination
              count={page?.pageCount}
              page={promocodeImportPageNumber}
              onChange={(event, n) => setPromocodeImportPageNumber(n)}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}
